import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AskLucidButton from 'features/agent-analytics/components/AskLucidButton';
import DrawerTab from 'shared/components/ui/DrawerTab';
import { Switch } from 'features/settings/components/SettingsRows';
import { ChevronRight, ExternalLink, Loader2, Copy, Check, BellPlus, ThumbsDown } from 'lucide-react';
import { openAsk } from 'shared/askDraft';
import { markedWrongPrompt } from 'features/evals3/components/markedWrongPrompt';
import { useParams } from 'react-router-dom';
import { useBreadcrumb, useHideGlobalTopBar } from 'shared/context/BreadcrumbContext';
import { getLineage, listRuns, getStats, getRun, getMyFeedback, submitFeedbackBulk, getNodeFeedbackPage, getQueueCounts, getSteps, patchLineage } from 'features/evals3/api/evals3Api';
import StepFilter from 'features/evals3/components/StepFilter';
import Eval3VerdictFeedback from 'features/evals3/components/Eval3VerdictFeedback';
import StepFeedback from 'features/evals3/components/StepFeedback';
import VerdictReview from 'features/evals3/components/VerdictReview';
import { specOf, saidText, shouldText } from 'features/evals3/components/StepAnswer';
import AddToDatasetButton from 'features/datasets/components/AddToDatasetButton';
import BulkStepLabel from 'features/evals3/components/BulkStepLabel';
import Eval3AnalysisPanel from 'features/evals3/pages/Eval3AnalysisPanel';
import Eval3QueuePage from 'features/evals3/pages/Eval3QueuePage';
import ResultPath, { outcomeOf, VERDICT_DOT, ms, usd } from 'features/evals3/components/ResultPath';
import { decisionText } from 'features/evals3/components/decision';
import { displayName } from 'features/evals3/editor/graphModel';
import TableFooter from 'shared/components/tables/TableFooter';
import AgentRunDetailPage from 'features/agent-analytics/pages/AgentRunDetailPage';
import TraceDetailPage from 'features/agent-analytics/pages/TraceDetailPage';
import ConversationDetailDrawer from 'features/agent-analytics/components/ConversationDetailDrawer';
import SegmentedTabs from 'shared/components/ui/SegmentedTabs';
import { tableHeaderRowClass, tableHeaderCellClass, tableRowClass, tableDataCellClass, activityToolbarButtonClass, dataTableFocusRing } from 'features/activity/styles/dataTableStyles';
import BrandLoader from 'shared/components/ui/BrandLoader';
import { usePermissions, PERMISSIONS } from 'shared/hooks/usePermissions';

/**
 * An eval's home: what it decided (TRO-427).
 *
 * Opening an eval from the list lands here, not in the editor — results are
 * what people come back to; editing is a mode they step into ("Edit"), and
 * the build-time controls (settings, structure, test, save) live there.
 * Versions are on both: here to see what is in production and move the
 * label, there to build.
 *
 *   Verdicts   every result in the window, newest first, filterable by
 *              outcome; the count on the right is the count of what is
 *              listed. Open one for its path. Select several to mark them
 *              wrong together.
 *   Queue      this eval's items waiting for a person.
 *   Analysis   the same window as numbers and charts.
 *
 * One Filter narrows them: time period, version, outcome (Verdicts only) and
 * the steps or path a run went through. It starts at the last 30 days, every
 * version, every outcome and every run. Analysis always shows the versions
 * side by side.
 */

const th = tableHeaderCellClass;
const td = tableDataCellClass;
const LIMIT = 50;
// Header actions: neutral, labelled; Edit is the one primary. Versions live in edit mode.
const secondaryBtn = `inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[12px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover ${dataTableFocusRing}`;
const primaryBtn = `inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-brand px-3 text-[12px] font-medium text-ink-onBrand transition-colors hover:bg-brand-hover ${dataTableFocusRing}`;

function when(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const sec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60); if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60); if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24); if (day < 30) return `${day}d ago`;
  return d.toLocaleDateString();
}

function verdictText(run) {
  const o = outcomeOf(run);
  const word = { passed: 'Pass', failed: 'Fail', errored: 'Errored', waiting: run.state === 'pending' ? 'Waiting' : run.state === 'skipped' ? 'Skipped' : 'Running' }[o];
  const decision = run.state === 'resolved' ? decisionText(run) : null;
  return [word, decision, run.reason_code].filter(Boolean).join(' · ');
}

const VERDICT_TONE = {
  passed: 'bg-success-soft text-success', failed: 'bg-danger-soft text-danger', errored: 'bg-warning-soft text-warning', waiting: 'bg-info-soft text-info',
};

/** The verdict as a tag: a dot for the outcome, the reason beside it. */
function Verdict({ run }) {
  const o = outcomeOf(run);
  return (
    <span className={`inline-flex max-w-full items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ${VERDICT_TONE[o]}`} title={run.state_reason || undefined}>
      <span aria-hidden className={`h-1.5 w-1.5 shrink-0 rounded-full ${VERDICT_DOT[o]}`} />
      <span className="truncate">{verdictText(run)}</span>
    </span>
  );
}

/** What a result is about: the run, span or conversation it scored. */
const UNIT = { run: 'Run', span: 'Span', conversation: 'Conversation' };
function unitOf(run) {
  const kind = run.target_kind || 'run';
  const id = kind === 'run' ? run.run_id : kind === 'span' ? run.span_id : run.conversation_id;
  return { kind, id: id == null ? null : String(id) };
}
const shortId = (id) => (id && id.length > 12 ? `${id.slice(0, 8)}…` : id);

function CopyId({ id }) {
  const [done, setDone] = useState(false);
  if (!id) return null;
  return (
    <button type="button" aria-label="Copy id" title="Copy id"
      onClick={() => { navigator.clipboard?.writeText(id).then(() => { setDone(true); setTimeout(() => setDone(false), 1200); }).catch(() => {}); }}
      className="rounded p-0.5 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]">
      {done ? <Check className="h-3 w-3" aria-hidden /> : <Copy className="h-3 w-3" aria-hidden />}
    </button>
  );
}

const FILTERS = {
  all: {}, passed: { state: 'resolved', passed: true }, failed: { state: 'resolved', passed: false }, errored: { state: 'errored' },
};

export default function Eval3ResultsPage({ teamId, lineageId, onBack, onEdit, onChanged, initialRunId = null, initialFilter = null }) {
  useBreadcrumb([]);
  useHideGlobalTopBar(true);
  const canWrite = usePermissions().can(PERMISSIONS.APP_USE);
  const { team } = useParams();
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState(30);
  const [runs, setRuns] = useState({ runs: [], total: 0 });
  const [filter, setFilter] = useState(initialFilter && FILTERS[initialFilter] ? initialFilter : 'all');
  const [version, setVersion] = useState('');       // '' = every version
  const reqRef = useRef(0);
  const [toggling, setToggling] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Rows expand IN PLACE (no side drawer): eval run id → { steps, queue, error } once opened.
  const [expanded, setExpanded] = useState({});
  // A run linked to from elsewhere that is not on this page: shown pinned above the table.
  const [pinned, setPinned] = useState(null);       // { run, steps, queue, error }
  const [overlay, setOverlay] = useState(null);     // { kind: 'run'|'span'|'conversation', id }
  const [feedback, setFeedback] = useState({});     // eval run id → the caller's own opinion
  const [nodeLabels, setNodeLabels] = useState({}); // eval run id → [step corrections by the team]
  const [reviewing, setReviewing] = useState({});   // eval run id → true while its step review is open
  const [meId, setMeId] = useState(null);            // the caller's user id, to tell their step labels from the team's
  // Analysis first; a link that names a run or an outcome is about verdicts, so it opens there.
  const [tab, setTab] = useState(initialRunId || (initialFilter && FILTERS[initialFilter]) ? 'verdicts' : 'analysis'); // analysis | verdicts | queue
  const [queueCount, setQueueCount] = useState(null);
  const [picked, setPicked] = useState(() => new Set());   // failing results chosen to file as a pattern
  const [findingTitle, setFindingTitle] = useState('');
  const [bulkShould, setBulkShould] = useState('flip');  // what the picked results should have been: flip each, all pass, all fail
  const [filing, setFiling] = useState(false);
  const [filed, setFiled] = useState(null);
  const [through, setThrough] = useState([]);        // the Filter: step ids every shown run went through
  const [stepOptions, setStepOptions] = useState({ steps: [], paths: [] });
  const throughKey = through.join(',');

  const loadLineage = useCallback(async () => {
    try { setData(await getLineage(teamId, lineageId)); }
    catch (e) { setError(e?.response?.data?.error || e.message); }
  }, [teamId, lineageId]);
  useEffect(() => { loadLineage(); }, [loadLineage]);
  useEffect(() => {
    Promise.resolve(getStats(teamId, lineageId, days, through, version ? Number(version) : null)).then(setStats).catch(() => setStats(null));
  }, [teamId, lineageId, days, throughKey, version]); // eslint-disable-line react-hooks/exhaustive-deps
  // What the Filter offers: every step and path in the window, whatever is picked.
  useEffect(() => {
    let cancelled = false;
    Promise.resolve(getSteps(teamId, lineageId, days, []))
      .then((d) => { if (!cancelled && d) setStepOptions({ steps: d.steps || [], paths: d.paths || [] }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [teamId, lineageId, days]);
  useEffect(() => {
    getQueueCounts(teamId)
      .then((c) => setQueueCount((c?.counts || []).find((x) => x.lineage_id === lineageId)?.open_items ?? 0))
      .catch(() => setQueueCount(null));
  }, [teamId, lineageId]);

  const graph = data?.current?.graph;
  const lineage = data?.lineage;
  const productionVersion = lineage?.production_version ?? data?.current?.version ?? null;
  const versions = (data?.versions || []).map((v) => v.version).sort((a, b) => b - a);
  const decidedOnPage = runs.runs.filter((r) => r.state === 'resolved');
  const allPicked = decidedOnPage.length > 0 && decidedOnPage.every((r) => picked.has(r.id));


  const query = useMemo(() => ({ ...FILTERS[filter], version: version ? Number(version) : undefined, days, through: throughKey ? throughKey.split(',') : [] }), [filter, version, days, throughKey]);

  // 50 at a time from the server; "Load more" asks for the next 50 and appends.
  const fetchPage = useCallback(async (from) => {
    const id = ++reqRef.current;
    setLoading(true);
    try {
      const page = await listRuns(teamId, lineageId, { ...query, limit: LIMIT, offset: from });
      if (id !== reqRef.current) return;
      setRuns((cur) => {
        if (!from) return page;
        const seen = new Set(cur.runs.map((r) => r.id));
        return { runs: [...cur.runs, ...page.runs.filter((r) => !seen.has(r.id))], total: page.total };
      });
      setError(null);
      const ids = page.runs.filter((r) => r.state === 'resolved').map((r) => r.id);
      if (ids.length) {
        getMyFeedback(teamId, ids).then((fb) => setFeedback((m) => ({ ...m, ...fb }))).catch(() => {});
        Promise.resolve(getNodeFeedbackPage(teamId, ids)).then((page) => {
          setNodeLabels((m) => ({ ...m, ...(page?.labels || {}) }));
          if (page?.me != null) setMeId(page.me);
        }).catch(() => {});
      }
    }
    catch (e) { if (id === reqRef.current) setError(e?.response?.data?.error || e.message); }
    finally { if (id === reqRef.current) setLoading(false); }
  }, [teamId, lineageId, query]);
  useEffect(() => { fetchPage(0); }, [fetchPage]);

  const setEnabled = async (next) => {
    if (toggling) return;
    setToggling(true);
    try { await patchLineage(teamId, lineageId, { enabled: next }); await loadLineage(); onChanged?.(); }
    catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setToggling(false); }
  };

  // A row opens inline: the path with per-step corrections under it. Clicking again folds it.
  const open = async (run) => {
    if (expanded[run.id]) { setExpanded((m) => { const n = { ...m }; delete n[run.id]; return n; }); return; }
    setExpanded((m) => ({ ...m, [run.id]: { steps: null } }));
    try {
      const full = await getRun(teamId, run.id);
      setExpanded((m) => (m[run.id] ? { ...m, [run.id]: { steps: full.steps || [], queue: full.queue || null, nodes: full.nodes || null } } : m));
    } catch (e) {
      setExpanded((m) => (m[run.id] ? { ...m, [run.id]: { steps: [], error: e?.response?.data?.error || e.message } } : m));
    }
  };
  // A thumbs-down on the verdict opens the row with the step review on top.
  const startReview = (run) => {
    setReviewing((m) => ({ ...m, [run.id]: true }));
    if (!expanded[run.id]) open(run);
  };
  const stopReview = (runId) => setReviewing((m) => { const n = { ...m }; delete n[runId]; return n; });
  const setNodeLabel = (runId, nodeId, saved) => setNodeLabels((m) => {
    const list = (m[runId] || []).filter((l) => !(l.node_id === nodeId && (saved ? String(l.user_id) === String(saved.user_id) : meId == null || String(l.user_id) === String(meId))));
    return { ...m, [runId]: saved ? [...list, saved] : list };
  });
  // The caller's own label on a step (the team's labels are shown too, but only yours is editable).
  const myLabel = (runId, nodeId) => (nodeLabels[runId] || []).find((l) => l.node_id === nodeId && (meId == null || String(l.user_id) === String(meId))) || null;
  // The node as the version that GRADED this run declared it (its options), falling back to today's graph.
  const nodeFor = (run, id) => expanded[run.id]?.nodes?.[id] || (pinned?.run?.id === run.id ? pinned.nodes?.[id] : null) || graph?.nodes?.[id];
  const stepExtraFor = (run) => (step) => (
    <StepFeedback teamId={teamId} run={run} step={step} node={nodeFor(run, step.node_id)} value={myLabel(run.id, step.node_id)}
      onChange={(saved) => setNodeLabel(run.id, step.node_id, saved)} />
  );
  // The review saved: keep the labels and the verdict opinion, close it, and hand the question to Lucid.
  const reviewFor = (run, opened) => (reviewing[run.id] && opened?.steps ? (
    <VerdictReview teamId={teamId} run={run} steps={opened.steps} nodeFor={(id) => nodeFor(run, id)}
      labels={Object.fromEntries((opened.steps || []).map((s) => [s.node_id, myLabel(run.id, s.node_id)]).filter(([, v]) => v))}
      feedback={feedback[run.id] || null}
      onCancel={() => stopReview(run.id)}
      onSaved={({ feedback: f, labels, fixKind, steps }) => {
        setFeedback((m) => ({ ...m, [run.id]: f }));
        labels.forEach((row) => setNodeLabel(run.id, row.node_id, row));
        stopReview(run.id);
        askAboutMarkedWrong([{ run, feedback: f, fixKind, steps }]);
      }} />
  ) : null);
  const togglePick = (id) => setPicked((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Results marked wrong are a question for Ask: it opens beside the page with
  // a drafted message — the results, what the eval said, what the person says
  // it should have been and why — for them to send.
  const askAboutMarkedWrong = (marked, reason = '') => {
    setFiled(null);
    const results = marked.map(({ run: r, feedback: f, fixKind = null, steps = null }) => {
      const unit = unitOf(r);
      return {
        id: r.id,
        said: verdictText(r),
        version: r.version,
        path: (r.path || []).map((id) => (graph ? displayName(graph, id) : id)).join(' → '),
        unit: unit.id ? `${UNIT[unit.kind].toLowerCase()} ${unit.id}` : null,
        correctedPassed: f?.corrected_passed ?? !r.passed,
        reason: f?.reason_text || '',
        fixKind,
        steps: Array.isArray(steps) ? steps.map(({ step, node, answer, label }) => ({
          name: node?.name || step.node_id,
          said: saidText(specOf(node), step.output?.value ?? step.value ?? null),
          right: answer?.agree === true,
          shouldHaveBeen: answer?.agree ? null : shouldText(specOf(node), answer),
          reason: label?.reason || null,
        })) : undefined,
      };
    });
    openAsk(markedWrongPrompt({ lineage: { ...lineage, production_version: productionVersion }, results, reason }));
  };

  // Several at once: one reason for all; each corrected to the other outcome, or all to one.
  const markPickedWrong = async () => {
    const reason = findingTitle.trim();
    if (!picked.size || !reason || filing) return;
    const rows = runs.runs.filter((r) => picked.has(r.id) && r.state === 'resolved');
    if (!rows.length) { setFiled({ ok: false, text: 'Only decided results can be marked wrong.' }); return; }
    setFiling(true);
    setFiled(null);
    try {
      const target = (r) => (bulkShould === 'pass' ? true : bulkShould === 'fail' ? false : !r.passed);
      // One request for the lot (≤ 200, the queue's cap): the server writes each on its own and lists what it could not.
      const out = await submitFeedbackBulk(teamId, rows.map((r) => ({ eval_run_id: r.id, thumbs: 'down', corrected_passed: target(r), reason_text: reason })));
      const saved = Array.isArray(out?.feedback) ? out.feedback : [];
      const byRun = new Map(saved.map((f) => [f.eval_run_id, f]));
      setFeedback((m) => ({ ...m, ...Object.fromEntries(saved.map((f) => [f.eval_run_id, f])) }));
      const marked = rows.filter((r) => byRun.has(r.id));
      const skipped = picked.size - marked.length;
      if (marked.length) askAboutMarkedWrong(marked.map((r) => ({ run: r, feedback: byRun.get(r.id) })), reason);
      setFiled({ ok: true, text: `Marked ${marked.length} wrong${skipped ? ` (${skipped} left out)` : ''}.${marked.length ? ' Lucid has them.' : ''}` });
      setPicked(new Set());
      setFindingTitle('');
    } catch (e) {
      setFiled({ ok: false, text: e?.response?.data?.message || e?.response?.data?.error || e.message });
    } finally { setFiling(false); }
  };

  // Arriving from elsewhere (a suggestion's evidence, a proposal) with one run in mind: open its path,
  // pinned above the table when it is not on this page.
  useEffect(() => {
    if (!initialRunId) return;
    setPinned({ run: { id: initialRunId }, steps: null });
    Promise.resolve(getRun(teamId, initialRunId))
      .then((full) => setPinned({ run: full.run || { id: initialRunId }, steps: full.steps || [], queue: full.queue || null, nodes: full.nodes || null }))
      .catch((e) => setPinned({ run: { id: initialRunId }, steps: [], error: e?.response?.data?.error || e.message }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRunId, teamId]);
  const pinnedOnPage = Boolean(pinned && runs.runs.some((r) => r.id === pinned.run.id));
  // On this page: open it in place once its steps have loaded (they usually
  // arrive after the page does), and bring it into view.
  const pinnedSteps = pinned?.steps || null;
  useEffect(() => {
    if (!pinnedOnPage || !pinned || !pinnedSteps || expanded[pinned.run.id]) return;
    setExpanded((m) => ({ ...m, [pinned.run.id]: { steps: pinnedSteps, queue: pinned.queue, nodes: pinned.nodes } }));
    const row = document.querySelector(`tr[data-result-id="${pinned.run.id}"]`);
    if (row && typeof row.scrollIntoView === 'function') row.scrollIntoView({ block: 'center' });
  }, [pinnedOnPage, pinnedSteps]); // eslint-disable-line react-hooks/exhaustive-deps

  // The ticked results as a dataset selection, by the unit each scored.
  const pickedSelection = (() => {
    const sel = { span_ids: [], run_ids: [], conversation_ids: [], eval_run_ids: [] };
    for (const r of runs.runs) {
      if (!picked.has(r.id)) continue;
      if (r.state === 'resolved') sel.eval_run_ids.push(r.id);
      const u = unitOf(r);
      if (!u.id) continue;
      if (u.kind === 'span') sel.span_ids.push(u.id); else if (u.kind === 'run') sel.run_ids.push(u.id); else sel.conversation_ids.push(u.id);
    }
    return sel;
  })();


  const enabled = lineage ? lineage.enabled !== false : true;
  const filterProps = {
    steps: stepOptions.steps, paths: stepOptions.paths, value: through, onChange: setThrough,
    ...(tab === 'queue' ? {} : { days, onDays: setDays, versions, productionVersion, version, onVersion: setVersion }),
    ...(tab === 'verdicts' ? { outcome: filter, onOutcome: setFilter } : {}),
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-[color:var(--surface-base)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-[color:var(--border-subtle)] px-4">
        <nav aria-label="Breadcrumb" className="flex min-w-0 flex-1 items-center gap-1.5">
          <button type="button" onClick={onBack} className="shrink-0 text-[13px] text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]">Evals</button>
          <ChevronRight className="h-3 w-3 shrink-0 text-[color:var(--text-muted)]" aria-hidden />
          <h1 className="min-w-0 truncate text-[13px] font-medium text-[color:var(--text-primary)]" aria-current="page" title={lineage?.name}>{lineage?.name || '…'}</h1>
        </nav>
        {/* The settings Switch, with its state in words; the label is part of
            the target, and a divider keeps it apart from the actions. */}
        {lineage && (
          <label className={`mr-1 inline-flex h-8 shrink-0 ${canWrite ? 'cursor-pointer' : ''} items-center gap-2 border-r border-[color:var(--border-subtle)] pr-3 text-[12px] text-[color:var(--text-secondary)]`}
            title={enabled ? 'Enabled: new results are scored' : 'Disabled: nothing new is scored'}>
            <Switch checked={enabled} onChange={setEnabled} disabled={toggling || !canWrite} label="Enabled" />
            <span aria-hidden>{enabled ? 'Enabled' : 'Disabled'}</span>
          </label>
        )}
        <AskLucidButton prompt={`About the "${lineage?.name || 'this'}" eval: `} title="Ask Lucid about this eval" />
        {canWrite && (
          <a href={team ? `/${encodeURIComponent(team)}/alerts/new?scope=eval&eval=${lineageId}` : undefined} className={secondaryBtn}
            title="Create an alert on this eval">
            <BellPlus className="h-3.5 w-3.5" aria-hidden /> Alert
          </a>
        )}
        {canWrite && <button type="button" onClick={() => onEdit?.()} className={primaryBtn}>Edit</button>}
      </header>
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-[color:var(--border-default)] px-4">
        {/* Sections of one eval, so the drawers' section tabs, not a grain switch. */}
        <div role="tablist" aria-label="Results view" className="flex items-center gap-0.5">
          {[
            { id: 'analysis', label: 'Analysis' },
            { id: 'verdicts', label: 'Verdicts' },
            { id: 'queue', label: 'Queue', badge: queueCount || null },
          ].map((t) => <DrawerTab key={t.id} role="tab" id={t.id} label={t.label} badge={t.badge} active={tab === t.id} onClick={setTab} />)}
        </div>
        <div className="min-w-0 flex-1" />
        {tab !== 'queue' && <StepFilter {...filterProps} />}
      </div>

      <div className="relative flex min-h-0 flex-1 flex-col">
        {tab === 'analysis' && (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <Eval3AnalysisPanel teamId={teamId} lineageId={lineageId} days={days} stats={stats} through={through} onThroughChange={setThrough}
              version={version ? Number(version) : null} onVersionChange={(v) => setVersion(v == null ? '' : String(v))} />
          </div>
        )}

        {tab === 'queue' && (
          <div className="min-h-0 flex-1">
            <Eval3QueuePage embedded teamId={teamId} lineageId={lineageId} onCounted={setQueueCount} />
          </div>
        )}

        {tab === 'verdicts' && (
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-2">
            {filed && (
              <div role={filed.ok ? 'status' : 'alert'} aria-label="Filed" className={`pb-2 text-[12px] ${filed.ok ? 'text-[color:var(--text-secondary)]' : 'text-danger'}`}>{filed.text}</div>
            )}
            {error && <div role="alert" className="mb-2 rounded-md border border-[color:var(--feedback-error)] bg-danger-soft p-2 text-[12px] text-danger">{error}</div>}

            {pinned && !pinnedOnPage && (
              <div className="mb-3 rounded-md border border-[color:var(--border-default)] px-3 pb-3 pt-2" role="region" aria-label="The result you came for">
                <div className="mb-1 text-[11px] text-[color:var(--text-muted)]">The result you came for, outside this window:</div>
                <ExpandedResult run={pinned.run} opened={pinned} graph={graph} productionVersion={productionVersion}
                  onOpenUnit={(kind, id) => setOverlay({ kind, id })} stepExtra={pinned.run.state ? stepExtraFor(pinned.run) : null}
                  review={pinned.run.state ? reviewFor(pinned.run, pinned) : null}
                  feedback={pinned.run.state === 'resolved' ? (
                    <Eval3VerdictFeedback teamId={teamId} verdictId={pinned.run.id} value={feedback[pinned.run.id]} onChange={(f) => setFeedback((m) => ({ ...m, [pinned.run.id]: f }))} onReview={() => startReview(pinned.run)} />
                  ) : null} />
              </div>
            )}
            {/* Loading and empty look as they do on the Queue tab beside it. */}
            {runs.runs.length === 0 && loading ? (
              <div className="flex justify-center py-12"><BrandLoader label="Loading results" /></div>
            ) : runs.runs.length === 0 ? (
              <div className="mt-1 flex h-40 items-center justify-center rounded-md border border-dashed border-[color:var(--border-default)] text-[12px] text-[color:var(--text-muted)]">
                {!enabled && !error ? 'Disabled, so nothing new is scored.' : 'No results in this window yet.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] table-fixed text-left">
                  <caption className="sr-only">Results of {lineage?.name || 'this eval'}, newest first</caption>
                  <thead>
                    <tr className={tableHeaderRowClass}>
                      <th scope="col" className={`${th} w-8`}>
                        <span className="sr-only">Select</span>
                        {decidedOnPage.length > 0 && (
                          <input type="checkbox" checked={allPicked} aria-label={allPicked ? 'Clear the selection' : 'Select every decided result on this page'}
                            title={allPicked ? 'Clear the selection' : 'Select every decided result on this page'}
                            onChange={() => setPicked(allPicked ? new Set() : new Set(decidedOnPage.map((r) => r.id)))} />
                        )}
                      </th>
                      <th scope="col" className={`${th} w-[112px]`}>{UNIT[lineage?.target] || 'Unit'}</th>
                      <th scope="col" className={`${th} w-[30%]`}>Verdict</th>
                      <th scope="col" className={th}>Path</th>
                      <th scope="col" className={`${th} w-[84px] text-right`}>When</th>
                      <th scope="col" className={`${th} w-[88px] text-right`}>Correct?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.runs.map((r) => {
                      const unit = unitOf(r);
                      const path = (r.path || []).map((id) => (graph ? displayName(graph, id) : id)).join(' → ');
                      const isOpen = Boolean(expanded[r.id]);
                      return (
                        <React.Fragment key={r.id}>
                        <tr onClick={() => open(r)} tabIndex={0} aria-expanded={isOpen} data-result-id={r.id}
                          onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); open(r); } }}
                          aria-label={`${UNIT[unit.kind]} ${unit.id || 'unknown'}: ${verdictText(r)}, v${r.version}, ${when(r.created_at)}`}
                          className={`${tableRowClass} ${isOpen ? 'bg-surface-hover' : ''} ${dataTableFocusRing} focus-visible:ring-inset`}>
                          <td className={td} onClick={(e) => e.stopPropagation()}>
                            {r.state === 'resolved' && (
                              <input type="checkbox" checked={picked.has(r.id)} onChange={() => togglePick(r.id)} aria-label={`Select ${verdictText(r)}, ${when(r.created_at)}, to mark wrong`} title="Select" />
                            )}
                          </td>
                          <td className={td}>
                            {unit.id ? (
                              <button type="button" onClick={(e) => { e.stopPropagation(); setOverlay({ kind: unit.kind, id: unit.id }); }}
                                title={`Open ${unit.kind} ${unit.id}`} className="font-mono text-[11.5px] text-[color:var(--text-primary)] underline-offset-2 hover:underline">
                                {shortId(unit.id)}
                              </button>
                            ) : <span className="text-[color:var(--text-disabled)]">—</span>}
                          </td>
                          <td className={td}><Verdict run={r} /></td>
                          <td className={`${td} truncate text-[color:var(--text-secondary)]`} title={path}>{path || '—'}</td>
                          <td className={`${td} whitespace-nowrap text-right text-[color:var(--text-muted)]`} title={r.created_at ? new Date(r.created_at).toLocaleString() : undefined}>{when(r.created_at)}</td>
                          <td className={`${td} text-right`} onClick={(e) => e.stopPropagation()}>
                            {r.state === 'resolved'
                              ? <span className="inline-flex justify-end"><Eval3VerdictFeedback teamId={teamId} verdictId={r.id} value={feedback[r.id]} onChange={(f) => setFeedback((m) => ({ ...m, [r.id]: f }))} onReview={() => startReview(r)} /></span>
                              : null}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="border-b border-[color:var(--border-subtle)]">
                            <td colSpan={6} className="px-3 pb-3 pt-1">
                              <ExpandedResult run={r} opened={expanded[r.id]} graph={graph} productionVersion={productionVersion}
                                onOpenUnit={(kind, id) => setOverlay({ kind, id })} stepExtra={stepExtraFor(r)} review={reviewFor(r, expanded[r.id])} />
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <TableFooter shown={runs.runs.length} total={runs.total} hasMore={runs.runs.length < runs.total} loading={loading} onLoadMore={() => fetchPage(runs.runs.length)} />
            {/* Selected results: one bar floating over the list, so choosing more never moves the rows. */}
            {picked.size > 0 && (
              <div className="pointer-events-none sticky bottom-3 z-10 flex justify-center">
                <div className="pointer-events-auto flex max-w-full flex-wrap items-center gap-1.5 rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] px-3 py-2 shadow-token-lg" role="group" aria-label="Mark wrong">
                  <span className="text-[12px] font-medium tabular-nums text-[color:var(--text-primary)]">{picked.size} selected</span>
                  <span className="text-[11.5px] text-[color:var(--text-muted)]">should have been</span>
                  <SegmentedTabs size="sm" ariaLabel="What they should have been" value={bulkShould} onChange={setBulkShould}
                    options={[{ value: 'flip', label: 'The other way' }, { value: 'pass', label: 'Pass' }, { value: 'fail', label: 'Fail' }]} />
                  <input value={findingTitle} onChange={(e) => setFindingTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') markPickedWrong(); }}
                    placeholder="Why?" aria-label="Why these are wrong"
                    className="h-8 w-64 min-w-0 flex-1 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-2 text-[12px] text-[color:var(--text-primary)] outline-none focus:border-[color:var(--border-strong)]" />
                  <button type="button" onClick={markPickedWrong} disabled={!findingTitle.trim() || filing}
                    title="Mark wrong and ask Lucid why"
                    className={`${primaryBtn} disabled:opacity-50`}>
                    {filing ? <Loader2 className="h-3 w-3 animate-spin" /> : <ThumbsDown className="h-3 w-3" aria-hidden />} Mark {picked.size} wrong
                  </button>
                  <BulkStepLabel teamId={teamId} graph={graph} runIds={[...picked]}
                    onDone={(rows) => rows.forEach((row) => setNodeLabel(row.eval_run_id, row.node_id, row))} />
                  <AddToDatasetButton selection={pickedSelection} onDone={() => setPicked(new Set())} />
                  <button type="button" onClick={() => setPicked(new Set())} className={activityToolbarButtonClass}>Clear</button>
                </div>
              </div>
            )}
          </div>
        )}

      </div>

      {overlay?.kind === 'run' && <div className="fixed inset-0 z-[220]"><AgentRunDetailPage teamId={teamId} runId={overlay.id} onClose={() => setOverlay(null)} /></div>}
      {overlay?.kind === 'span' && <div className="fixed inset-0 z-[220]"><TraceDetailPage teamId={teamId} spanId={overlay.id} onClose={() => setOverlay(null)} onRunClick={(id) => setOverlay({ kind: 'run', id })} onSpanClick={(id) => setOverlay({ kind: 'span', id })} /></div>}
      {overlay?.kind === 'conversation' && <div className="fixed inset-0 z-[220]"><ConversationDetailDrawer teamId={teamId} conversationId={overlay.id} onClose={() => setOverlay(null)} /></div>}
    </div>
  );
}

/**
 * One result, opened IN PLACE under its row: what was decided, the facts
 * (version, when, cost, agent), the unit it scored, and the path with a
 * "should have been" under every step. Replaces the side drawer (PRD §4.5).
 */
function ExpandedResult({ run, opened, graph, productionVersion, onOpenUnit, stepExtra, review = null, feedback = null }) {
  const unit = unitOf(run);
  const steps = opened?.steps || [];
  const totalMs = steps.reduce((n, x) => n + (Number(x.latency_ms) || 0), 0);
  const totalCost = usd(steps.reduce((n, x) => n + (Number(x.provider_cost_usd) || 0), 0) || run.provider_cost_usd);
  const facts = [
    run.version != null ? `v${run.version}${run.version === productionVersion ? ' · production' : ''}` : null,
    run.created_at ? `scored ${when(run.created_at)}` : null,
    totalMs > 0 ? ms(totalMs) : null,
    totalCost || null,
    run.agent_name || null,
  ].filter(Boolean);
  return (
    <div role="region" aria-label={`${UNIT[unit.kind]} ${unit.id || ''}: ${run.state ? verdictText(run) : 'result'}`} className="rounded-md bg-[color:var(--surface-base)] px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        {run.state ? <Verdict run={run} /> : null}
        <span className="text-[11px] text-[color:var(--text-muted)]">{facts.join(' · ')}</span>
        {feedback}
        <div className="flex-1" />
        {unit.id && (
          <>
            <span className="font-mono text-[11px] text-[color:var(--text-muted)]" title={unit.id}>{UNIT[unit.kind]} {shortId(unit.id)}</span>
            <CopyId id={unit.id} />
            <button type="button" onClick={() => onOpenUnit(unit.kind, unit.id)} className={activityToolbarButtonClass}>
              <ExternalLink className="h-3 w-3" aria-hidden /> Open {unit.kind}
            </button>
          </>
        )}
      </div>
      {opened?.error && <div className="pt-2 text-[12px] text-danger" role="alert">{opened.error}</div>}
      <div className="pt-2">
        {review}
        {opened?.steps == null
          ? <div className="flex justify-center py-6"><BrandLoader label="Loading the path" /></div>
          : <ResultPath run={run} steps={steps} graph={graph} queue={opened.queue} stepExtra={stepExtra} />}
      </div>
    </div>
  );
}
